/**
 * ShadowModeBadge: the persistent "nothing is sent" indicator.
 *
 * Norican I2Q runs in shadow mode, in parallel to Go Autonomous: it reads the intake
 * mailboxes and master data, drafts, and compares its drafts with what sales
 * actually replied, but it never sends, drafts, moves or writes anything outside its
 * own database. This badge keeps that visible on every page (app-shell header) and
 * in the Outlook pane.
 *
 * The tooltip text adds the outbound gate state from `GET /api/status`
 * (`egress_gate`, added by the live-data contract) when the backend reports it.
 */

import { Icon, ShieldCheck } from "@norican/icons";
import { Badge } from "@norican/react";

import { type LiveEngineStatus, useEngineStatus } from "@/lib/api";

export interface ShadowModeBadgeProps {
  /** Badge text; defaults to the header wording. */
  label?: string;
  size?: "xs" | "sm" | "md";
  /** Only the shield icon (phone header); the label stays the accessible name. */
  iconOnly?: boolean;
  className?: string;
}

export function ShadowModeBadge({
  label = "Shadow mode: read-only, nothing is sent",
  size = "sm",
  iconOnly = false,
  className,
}: ShadowModeBadgeProps) {
  const { data } = useEngineStatus();
  const gate = (data as LiveEngineStatus | undefined)?.egress_gate;
  const gateNote =
    gate === "enforce"
      ? " Outbound network gate: enforced."
      : gate === "off"
        ? " Outbound network gate: off in this environment."
        : "";

  return (
    <Badge
      tone="info"
      variant="subtle"
      size={size}
      className={className}
      title={`Runs beside Go Autonomous. I2Q sends, drafts and moves no mail; it writes nothing to F&O, IMDP or GoA.${gateNote}`}
      aria-label={iconOnly ? label : undefined}
      role={iconOnly ? "img" : undefined}
    >
      <span className="inline-flex items-center gap-1.5">
        <Icon icon={ShieldCheck} size="xs" />
        {!iconOnly && label}
      </span>
    </Badge>
  );
}
